import fp from 'lodash/fp';
import React, {
    ChangeEventHandler,
    useState,
    useCallback,
    useRef,
    useImperativeHandle,
    useMemo,
    useEffect,
} from 'react';
import { formatDate, formatDateTime, formatTime, parseDate } from '@factorin/util';
import InputMask, { Props as InputMaskProps } from 'react-input-mask';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { isValid } from 'date-fns';

import { cn, ClassName } from 'Modules/ui/utils';


import { Calendar, CalendarProps } from '../Calendar';
import styles from './index.module.scss';
import { Popup } from '../Popup';


export type CalendarInputProps = ClassName & Pick<CalendarProps, 'disabledDays'> & Omit<InputMaskProps, 'value'> & {
    locale: 'en' | 'ru';

    onFocus?: () => void;
    onBlur?: () => void;

    disabled?: boolean;
    value: Date;
    onChange: (value: Date) => void;

    inputClassname?: string;
    children?: React.ReactElement[];

    withTime?: boolean;
    timeZone?: string;
};

export type CalendarInputMethods = {
    close(): void;
};

const getTimeFromDateString = (date: string) => {
    const time = date?.split(', ')[1];
    return {
        time: time || null,
        hours: Number(time?.split(':')[0]) || null,
        minutes: Number(time?.split(':')[1]) || null,
    };
};

const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const CalendarInput = React.forwardRef<CalendarInputMethods, CalendarInputProps>(({
    className, locale,
    disabled, onFocus,
    onBlur, value, onChange,
    children, inputClassname,
    withTime, disabledDays,
    timeZone = USER_TIMEZONE,
    ...props
}, ref) => {
    const inputRef = useRef<HTMLInputElement>();

    const [isOpen, setIsOpen] = useState(false);
    const [currentTimeZone, setCurrentTimeZone] = useState<string>('UTC');
    const [inputValue, setInputValue] = useState<string>(null);

    const displayDate = isValid(value) ? utcToZonedTime(value, currentTimeZone) : null;

    useEffect(() => {
        try {
            Intl.DateTimeFormat(undefined, { timeZone });
            setCurrentTimeZone(timeZone);
        } catch (e) {
            console.warn('Некорректный часовой пояс');
        }
    }, [timeZone]);

    useImperativeHandle(ref, () => ({
        close: () => setIsOpen(false),
    }));

    const commitInputChange = useMemo(
        () => fp.throttle(200, (value: string) => {
            if (withTime) {
                const newDate = parseDate(value.split(',')[0], locale);
                newDate?.setHours(
                    getTimeFromDateString(value).hours, getTimeFromDateString(value).minutes, 0, 0,
                );
                onChange(newDate ? zonedTimeToUtc(newDate, currentTimeZone) : null);
            } else {
                const newDate = parseDate(value, locale);
                onChange(newDate ? zonedTimeToUtc(newDate, currentTimeZone) : null);
            }
        }),
        [onChange, locale, withTime, currentTimeZone],
    );

    const formatFunc = useMemo(() => withTime ? formatDateTime : formatDate, [withTime]);

    const handleInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
        e => {
            setInputValue(e.target.value);
            commitInputChange(e.target.value);
        },
        [commitInputChange],
    );

    const onInputBlur = useCallback(
        () => {
            setInputValue(null);
            if (onBlur && !isOpen)
                onBlur();
        },
        [onBlur, isOpen],
    );

    const onInputFocus = useCallback(
        () => {
            setIsOpen(true);
            if (onFocus)
                onFocus();
        },
        [onFocus],
    );

    const onCalendarChange = useCallback(
        (date: Date) => {
            const utcDate = zonedTimeToUtc(date, currentTimeZone);
            onChange(utcDate);
            setInputValue(null);
            if (!withTime) {
                setIsOpen(false);
                onBlur();
            }
        },
        [onChange, onBlur, withTime, currentTimeZone],
    );

    const onTimeChange = useCallback(
        e => {
            const [h, m] = e.target.value.split(':');
            const dateWithTime = value ? new Date(value) : new Date();
            dateWithTime.setHours(h, m, 0, 0);
            const utcDate = zonedTimeToUtc(dateWithTime, currentTimeZone);
            onChange(utcDate);
            setInputValue(null);
        },
        [onChange, value, currentTimeZone],
    );

    const onCalendarClose = useCallback(
        () => {
            onBlur();
            setIsOpen(false);
        },
        [onBlur],
    );
    return (
        <div className={cn(styles.calendarInput, className)}>
            <InputMask
                className={inputClassname}
                disabled={disabled}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                value={inputValue || formatFunc(displayDate, locale)}
                onChange={handleInputChange}
                mask={`99.99.9999${withTime ? ', 99:99' : ''}`}
                {...props}
            >
                {inputProps => <input ref={inputRef} disabled={disabled} {...inputProps} />}
            </InputMask>
            <Popup
                isOpen={isOpen}
                closingDelay={50}
                onClose={onCalendarClose}
                overlayZIndex={40}
                parentPosition={
                    inputRef.current && inputRef.current.getBoundingClientRect()}
            >
                <div className={styles.calendar} data-testid='calendar-popup'>
                    <Calendar
                        locale={locale}
                        value={displayDate}
                        onChange={onCalendarChange}
                        disabledDays={disabledDays}
                        {...props}
                    />
                    {withTime && (
                        <div className={styles.timeInputContainer}>
                            <input
                                data-testid='calendar-time'
                                type='time'
                                onChange={onTimeChange}
                                value={getTimeFromDateString(inputValue)?.time ||
                                    formatTime(displayDate)}
                                className={inputClassname}
                            />
                        </div>
                    )}
                    <div className={styles.children}>
                        {children}
                    </div>
                </div>
            </Popup>
        </div>
    );
});
